import * as React from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { useHotkeyOverlay } from '../data-mediation';
import { useBlankScreenState } from '../misc/overlays/blank-screen';
import { useToolbarVisiblityState } from './ToolbarVisiblityProvider';

export const Hotzones = ({ children }: React.PropsWithChildren) => {
  const showHotkeyOverlay = useHotkeyOverlay((state) => state.show);
  const { blankScreenVisible } = useBlankScreenState();
  if (showHotkeyOverlay || blankScreenVisible) return null;

  return children;
};

const HotzoneLeft = ({ ...rest }: BoxT) => {
  const { onMouseEnter, onMouseLeave } = useToolbarVisiblityState();
  return (
    <Box
      zIndex={5}
      position="absolute"
      left={0}
      bottom={140}
      width="3%"
      height="85%"
      data-testid="toolbar-hotzone-left"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...rest}
    />
  );
};

const HotzoneTopLeft = ({ ...rest }: BoxT) => {
  const { onMouseEnter, onMouseLeave } = useToolbarVisiblityState();
  return (
    <Box
      zIndex={5}
      position="absolute"
      left={0}
      top={0}
      width="20%"
      height="10%"
      data-testid="toolbar-hotzone-top-left"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...rest}
    />
  );
};

const HotzoneTopRight = ({ ...rest }: BoxT) => {
  const { onMouseEnter, onMouseLeave } = useToolbarVisiblityState();
  return (
    <Box
      zIndex={5}
      position="absolute"
      right={0}
      top={0}
      width="20%"
      height="10%"
      data-testid="toolbar-hotzone-top-right"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...rest}
    />
  );
};

const HotzoneBottomLeft = ({ ...rest }: BoxT) => {
  const { onMouseEnter, onMouseLeave } = useToolbarVisiblityState();
  return (
    <Box
      zIndex={5}
      position="absolute"
      left="8%"
      bottom={0}
      width="65%"
      height="9%"
      data-testid="toolbar-hotzone-bottom-left"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...rest}
    />
  );
};

/**
 * Used to extend the hotzone area to the children that is visible
 * in order to keep the hover state enable when hovering elements inside
 */
const HotzoneContentWrapper = ({ ...rest }: BoxT) => {
  const { onMouseEnter, onMouseLeave } = useToolbarVisiblityState();
  return (
    <Box
      zIndex={5}
      position="absolute"
      data-testid="toolbar-hotzone-content-wrapper"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...rest}
    />
  );
};

Hotzones.Left = HotzoneLeft;
Hotzones.TopLeft = HotzoneTopLeft;
Hotzones.TopRight = HotzoneTopRight;
Hotzones.BottomLeft = HotzoneBottomLeft;
Hotzones.ContentWrapper = HotzoneContentWrapper;

Hotzones.All = ({ children }: React.PropsWithChildren) => (
  <>
    <Hotzones.Left />
    <Hotzones.TopLeft />
    <Hotzones.TopRight />
    <Hotzones.BottomLeft />
    <Hotzones.ContentWrapper>{children}</Hotzones.ContentWrapper>
  </>
);
